import { T, Var } from 'gt-react';
import {
  ArrowsClockwiseIcon,
  InfoIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useRefreshTranslationList } from '@/api/rpc/hooks/integrations/useRefreshTranslationList';
import { useCan } from '@/hooks/useCan';

interface Props {
  visible: boolean;
  /** Persisted customer, or the suggested one when the order isn't confirmed yet. */
  customerId?: string;
  /** How many line items have no product. Drives the message; 0 falls back to generic copy. */
  unmatchedLineItemCount?: number;
  salesOrderDraftId: string;
  onRefreshed?: () => void;
}

/**
 * The translation-list status of an EDI draft, and the action to re-import it.
 *
 * Shown on every editable EDI draft, not only when lines are unmatched. Product
 * mapping is read-only for EDI (the translation list is the whole product
 * reference), so this banner carries the only in-app remedy — and the case that
 * needs it most, a line mapped to the *wrong* product, looks identical to a
 * correct one from here. Leaving the action reachable is the point.
 *
 * The "couldn't match" message is unconditional — an unmatched line must always be
 * visible on the order, even when there is no customer to refresh a list for (which
 * is itself the reason nothing matched). Only the Refresh action is gated, since it
 * needs a customer and a permission.
 *
 * Deliberately does NOT lead with "the translation list may be out of date": EDI
 * matching resolves solely from the customer's Famous EDI translation list, so the
 * common case is a SKU that was never on the list at all. Refreshing re-imports the
 * same list and changes nothing, which is exactly the dead end this wording used to
 * send people down.
 */
/**
 * What the banner says, which depends entirely on whether anything is unmatched.
 *
 * Its own component so the nested conditionals stay out of the banner's
 * cognitive-complexity budget.
 */
function BannerMessage({
  hasUnmatched,
  unmatchedLineItemCount,
  customerId,
}: {
  hasUnmatched: boolean;
  unmatchedLineItemCount?: number;
  customerId?: string;
}) {
  if (!hasUnmatched) {
    return (
      <span>
        <T context="EDI draft banner shown when every line matched: mapping is read-only and comes from the Famous translation list">
          Products on EDI orders come from this customer&apos;s translation list
          in Famous and cannot be changed here. If an item is missing or mapped
          to the wrong product, fix it in Famous, then refresh.
        </T>
      </span>
    );
  }

  return (
    <span>
      <T context="EDI draft banner: N line items have no matched product">
        <Var>{unmatchedLineItemCount}</Var> line item(s) could not be matched to
        a product.
      </T>{' '}
      {customerId ? (
        <T context="EDI draft banner: how to resolve an unmatched product">
          Add the item to this customer&apos;s translation list in Famous, then
          refresh.
        </T>
      ) : (
        <T context="EDI draft banner: no customer matched on the order yet">
          No customer is matched on this order yet, so there is no translation
          list to match against.
        </T>
      )}
    </span>
  );
}

export function TranslationRefreshBanner({
  visible,
  customerId,
  unmatchedLineItemCount,
  salesOrderDraftId,
  onRefreshed,
}: Props) {
  const refreshMutation = useRefreshTranslationList({
    onSuccess: () => onRefreshed?.(),
  });
  // Mirrors the backend's accepted grants: `Update:Customer` remains a
  // transitional fallback until `Update:EdiTranslationList` is granted in every
  // tenant. The diagnostic text stays visible either way — only the action is
  // gated, so users without the grant still see why the lines are unmatched.
  const [canRefreshTranslationList] = useCan(
    Action.Update,
    Subject.EdiTranslationList,
  );
  const [canUpdateCustomer] = useCan(Action.Update, Subject.Customer);
  const canRefresh = Boolean(
    customerId && (canRefreshTranslationList || canUpdateCustomer),
  );

  if (!visible) return null;

  // Nothing is wrong when every line matched, so the banner drops the amber
  // warning and reads as information: it is still shown because the mapping is
  // read-only for EDI, and re-importing the list is the only way to correct a
  // line that matched the *wrong* product — a case we cannot detect, since such
  // a line is indistinguishable from a correct one on our side.
  const hasUnmatched = Boolean(
    unmatchedLineItemCount && unmatchedLineItemCount > 0,
  );

  return (
    <AlertContainer
      className={
        hasUnmatched
          ? 'mb-3 border-amber-200 bg-amber-50'
          : 'mb-3 border-border bg-muted/50'
      }
    >
      {hasUnmatched ? (
        <WarningIcon className="h-4 w-4 text-amber-600" />
      ) : (
        <InfoIcon className="h-4 w-4 text-muted-foreground" />
      )}
      <AlertDescription
        className={`ml-2 flex flex-wrap items-center gap-3 text-sm ${
          hasUnmatched ? 'text-amber-900' : 'text-muted-foreground'
        }`}
      >
        <BannerMessage
          hasUnmatched={hasUnmatched}
          unmatchedLineItemCount={unmatchedLineItemCount}
          customerId={customerId}
        />
        {canRefresh && (
          <div className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              disabled={refreshMutation.isPending}
              onClick={() =>
                refreshMutation.mutate({
                  customerId,
                  stagedPurchaseOrderId: salesOrderDraftId,
                })
              }
            >
              <ArrowsClockwiseIcon
                className={`mr-2 h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`}
              />
              {refreshMutation.isPending ? (
                <T context="translation refresh button loading">
                  Refreshing...
                </T>
              ) : (
                <T context="translation refresh button">
                  Refresh Translation List
                </T>
              )}
            </Button>
          </div>
        )}
      </AlertDescription>
    </AlertContainer>
  );
}
